import { Badge, Group, Paper, Stack, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type {
  Bundle,
  Consent,
  Coverage,
  Organization,
  Patient,
  Questionnaire,
  QuestionnaireResponse,
} from '@medplum/fhirtypes';
import { Loading, useMedplum, useMedplumProfile } from '@medplum/react';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AddCardModal } from './AddCardModal';
import { ChargesSection } from './ChargesSection';
import { ConsentsSection } from './ConsentsSection';
import { syncPreferredContactMethodsToConsents } from './contactConsentSync';
import { InsuranceSection } from './InsuranceSection';
import { PasswordSection } from './PasswordSection';
import { PaymentSection } from './PaymentSection';
import { PersonalInfoSection } from './PersonalInfoSection';
import { countMissingRequiredConsents, friendlyErrorMessage, mergeIntakeInformedConsent } from './settings.utils';
import { useSetSettingsTaskCount } from './useSettingsTask';

export function AccountSettingsPage(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient;
  const [patient, setPatient] = useState<Patient>(profile);
  const [coverages, setCoverages] = useState<Coverage[]>([]);
  const [payorNames, setPayorNames] = useState<Record<string, string>>({});
  const [insuranceOptions, setInsuranceOptions] = useState<string[]>([]);
  const [consents, setConsents] = useState<Consent[]>([]);
  const [loading, setLoading] = useState(true);
  const [addCardOpen, setAddCardOpen] = useState(false);

  useEffect(() => {
    setPatient(profile);
  }, [profile]);

  useEffect(() => {
    const patientId = profile.id;
    if (!patientId) {
      return;
    }

    Promise.all([
      medplum.searchResources('Coverage', { beneficiary: `Patient/${patientId}` }),
      medplum.searchResources('Consent', { patient: `Patient/${patientId}` }),
      medplum.searchResources('QuestionnaireResponse', {
        subject: `Patient/${patientId}`,
        _sort: '-authored',
        _count: '50',
      }),
    ])
      .then(
        async ([coverages, fetchedConsents, questionnaireResponses]: [
          Coverage[],
          Consent[],
          QuestionnaireResponse[],
        ]) => {
          let syncedConsents: Consent[] = fetchedConsents;
          try {
            syncedConsents = await syncPreferredContactMethodsToConsents(medplum, profile, fetchedConsents);
          } catch (error) {
            Sentry.captureException(error);
          }
          setConsents(mergeIntakeInformedConsent(syncedConsents, questionnaireResponses, profile));

          const activeCoverages = coverages.filter((coverage) => coverage.status === 'active');
          setCoverages(activeCoverages);

          const entries = await Promise.all(
            activeCoverages.map(async (coverage) => {
              const fallback = coverage.payor?.[0]?.display ?? '';
              const payorRef = coverage.payor?.[0]?.reference;
              if (!coverage.id || !payorRef) {
                return coverage.id ? [coverage.id, fallback] : undefined;
              }
              try {
                const org = await medplum.readReference({ reference: payorRef } as { reference: string });
                return [coverage.id, (org as Organization).name ?? fallback] as const;
              } catch {
                // Payor org may not be readable - that's ok
                return [coverage.id, fallback] as const;
              }
            })
          );
          setPayorNames(
            Object.fromEntries(entries.filter((entry): entry is readonly [string, string] => Boolean(entry)))
          );
        }
      )
      .catch((error) => {
        showNotification({ color: 'red', message: friendlyErrorMessage(error) });
      })
      .finally(() => setLoading(false));
  }, [medplum, profile]);

  useEffect(() => {
    fetch('/public-questionnaires.json')
      .then((res) => res.json())
      .then((bundle: Bundle) => {
        const questionnaire = bundle.entry?.find(
          (entry) =>
            entry.resource?.resourceType === 'Questionnaire' &&
            (entry.resource as Questionnaire).name === 'self-referral-intake-form'
        )?.resource as Questionnaire | undefined;
        const options = questionnaire?.item
          ?.find((item) => item.linkId === 'insurance-provider')
          ?.answerOption?.map((option) => option.valueString)
          .filter((option): option is string => typeof option === 'string');
        setInsuranceOptions(options ?? []);
      })
      .catch(() => {
        setInsuranceOptions([]);
      });
  }, []);

  const handlePatientUpdated = useCallback((updated: Patient) => {
    setPatient(updated);
  }, []);

  const handleCoveragesUpdated = useCallback((updated: Coverage[]) => {
    setCoverages(updated);
  }, []);

  const handleConsentsUpdated = useCallback((updated: Consent[]) => {
    setConsents(updated);
  }, []);

  const setSettingsTaskCount = useSetSettingsTaskCount();
  const missingConsentCount = useMemo(() => countMissingRequiredConsents(consents), [consents]);

  useEffect(() => {
    setSettingsTaskCount(missingConsentCount);
  }, [missingConsentCount, setSettingsTaskCount]);

  if (loading) {
    return <Loading />;
  }

  return (
    <Stack gap="lg" p="lg" bg="#f8f9fb" mih="100%">
      <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
        <Group justify="space-between" align="center" mb="sm">
          <Title order={1} size="2rem" fw={700}>
            Account Settings
          </Title>
          {missingConsentCount > 0 && (
            <Badge color="red" size="lg" radius="xl">
              {missingConsentCount} {missingConsentCount === 1 ? 'Consent Required' : 'Consents Required'}
            </Badge>
          )}
        </Group>
      </Paper>
      <PersonalInfoSection patient={patient} onPatientUpdated={handlePatientUpdated} />
      <PasswordSection patient={patient} />
      <InsuranceSection
        patient={patient}
        coverages={coverages}
        payorNames={payorNames}
        insuranceOptions={insuranceOptions}
        onCoveragesUpdated={handleCoveragesUpdated}
      />
      <PaymentSection patient={patient} onOpenAddCard={() => setAddCardOpen(true)} />
      {patient.id && <ChargesSection patientId={patient.id} />}
      <ConsentsSection patient={patient} consents={consents} onConsentsUpdated={handleConsentsUpdated} />
      {patient.id && (
        <AddCardModal
          opened={addCardOpen}
          patientId={patient.id}
          onClose={() => setAddCardOpen(false)}
          onSaved={(updated) => {
            handlePatientUpdated(updated);
            setAddCardOpen(false);
          }}
        />
      )}
    </Stack>
  );
}
